import React, { useEffect, useRef, useState } from "react";
import { DropdownMenu, Icon } from "@astryxdesign/core";
import type { IconSize } from "@astryxdesign/core";
import { Plus } from "lucide-react";
import styles from "./InsertZone.module.css";

export interface InsertZoneProps {
  /** Inserts a blank question at exactly this gap's position. */
  onInsertQuestion: () => void;
  /** Inserts a new section immediately after this gap's section. */
  onInsertSection: () => void;
  /**
   * Both variants are a fixed 24px today — kept for any future divergence
   * (currently only the plus glyph's size differs: `"compact"` sizes it for
   * the NavRail's tighter rows).
   * @default "default"
   */
  variant?: "default" | "compact";
  /**
   * Fully suppresses reveal and interaction — used while a drag-and-drop
   * reorder is in progress, so gaps don't sprout a plus mid-drag. Forces
   * any already-open chooser closed.
   * @default false
   */
  isDisabled?: boolean;
}

// Tolerance (px) around the zone's live bounding rect for the stuck-hover
// self-heal below — the pointer resting right at the edge shouldn't
// flicker collapsed/expanded from sub-pixel rect measurement.
const STUCK_HOVER_TOLERANCE_PX = 4;

/**
 * Fixed-height (24px, both variants) insertion affordance rendered in the
 * gap between two cards (or rail rows). `.zone` is in-flow and carries all
 * of the surrounding VStack's inter-row spacing itself
 * (`script_builder.tsx` sets the relevant gaps to `0`) — reveal is
 * opacity/tint only, immediate on `mouseenter`/`mouseleave`, softened by
 * nothing but a short CSS fade (~120ms, `--duration-fast`).
 *
 * An earlier version animated height on hover-intent (near-zero → expanded),
 * damped by JS timers to avoid oscillation as rows/cards shifted under the
 * pointer mid-transition. Both the rail (packed rows) and the form
 * (same problem, just roomier) confirmed via testing that an *expanding*
 * gap makes the plus itself a moving, hard-to-click target — so there is no
 * height animation left at all, and no expand/collapse timers either
 * (nothing left to damp against). `isExpanded`/`.expanded` (the state/CSS
 * class name) predate this change and are kept as-is to avoid a disruptive
 * rename across the file — read them as "revealed", not literal expansion.
 *
 * The chooser ("Question" / "Section") is Astryx's `DropdownMenu` in
 * controlled mode, opened only by clicking anywhere on the zone (including
 * the plus — a click there is DropdownMenu's own trigger click, so the
 * zone's own click handler ignores it to avoid double-toggling). Selecting
 * an option inserts, then immediately clears every bit of reveal state
 * (menu closed, zone hidden) rather than waiting on `DropdownMenu`'s own
 * delayed close.
 *
 * **The refocus race.** `DropdownMenu` returns DOM focus to its trigger on
 * *every* close — confirmed from its actual source (not just the `.d.ts`):
 * `useLayer`'s `hide()` calls `onHide?.()` synchronously as its last
 * statement (no animation-gated delay), and `DropdownMenu`'s `onHide`
 * handler (`handleLayerHide`) ends with `buttonRef.current?.focus()`. Since
 * selecting a menu item runs `onClick()` (our handler, which blurs) and
 * *then* `ctx.closeMenu()` (which triggers that same synchronous refocus)
 * back-to-back in one call stack, a same-tick `blur()` always loses that
 * race — Astryx's `focus()` runs after it and wins. Fixed by deferring our
 * blur to a `setTimeout(0)` macrotask, which is provably ordered after the
 * entire synchronous close/refocus chain above (no `requestAnimationFrame`
 * needed, since nothing here waits on a paint or a CSS transition).
 *
 * That refocus is also what caused a linger bug: some browsers'
 * `:focus-visible` heuristics apparently still qualify that programmatic,
 * pointer-triggered refocus (Astryx's own outline is already
 * `:focus-visible`-scoped, yet a ring was still visible in testing), and
 * this component's own keyboard-reveal rule was `:has(:focus-visible)`
 * alone. Two more layers close that gap: (1) the deferred blur only fires
 * when the *last interaction with this zone* was pointer-driven — tracked
 * via `data-interaction` on the zone root, set to `"pointer"` on
 * `pointerdown`, and to `"keyboard"` on any `keydown` within the zone or a
 * `focus` event whose target itself matches `:focus-visible` (covering a
 * fresh Tab-in, which arrives without a prior `keydown` inside the zone) —
 * so real keyboard users always keep focus (and their ring) after Escape,
 * per WCAG 2.4.3. (2) `InsertZone.module.css` additionally gates *both* the
 * band's `:has(:focus-visible)` reveal and the trigger button's own
 * `:focus-visible` outline behind `[data-interaction="keyboard"]`, so a
 * stray focus-visible match can never re-reveal anything after a
 * pointer-driven interaction even if a future Astryx/browser change shifts
 * the refocus timing again.
 *
 * The band stays gone after an insert unless the pointer is genuinely
 * still over the gap, in which case a fresh `mouseenter` reveals it again.
 *
 * This MUST be rendered inside an Astryx <Theme> scope.
 */
const InsertZone = ({
  onInsertQuestion,
  onInsertSection,
  variant = "default",
  isDisabled = false,
}: InsertZoneProps): React.ReactElement => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Tracks live hover state independent of isExpanded, so a menu close that
  // isn't the item-selection path (Escape, outside click) knows whether to
  // hide immediately or leave the reveal up (the pointer is still over it).
  const isPointerOverRef = useRef(false);
  const triggerWrapRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);

  // Writes the last-interaction modality directly to the zone's own
  // `data-interaction` DOM attribute rather than through React state — it
  // only ever needs to be *read* synchronously (by handleSelect/
  // handleOpenChange and by CSS), never to drive a re-render, so a ref-style
  // imperative write keeps pointermove-adjacent handlers cheap.
  const setInteractionModality = (modality: "pointer" | "keyboard") => {
    if (zoneRef.current) {
      zoneRef.current.dataset.interaction = modality;
    }
  };

  const handleZonePointerDown = () => {
    setInteractionModality("pointer");
  };

  const handleZoneKeyDown = () => {
    setInteractionModality("keyboard");
  };

  // Catches a fresh Tab-in specifically: the Tab keypress that *moves* focus
  // into the zone originates on whatever element had focus before (outside
  // the zone), so it never reaches handleZoneKeyDown above. Checking
  // `:focus-visible` on the actual focused element at the moment of arrival
  // is the browser's own "was this keyboard-ish" heuristic — true for a real
  // Tab, false for DropdownMenu's programmatic post-close refocus (the
  // scenario this whole modality-tracking exists to distinguish).
  const handleZoneFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.matches(":focus-visible")) {
      setInteractionModality("keyboard");
    }
  };

  // Blurs the trigger, but only for a pointer-driven interaction — a
  // keyboard user must keep focus (and its visible ring) on the trigger
  // after closing the menu, or focus gets stranded (WCAG 2.4.3). Deferred to
  // a macrotask so it runs after DropdownMenu's own synchronous
  // refocus-on-close; see the module JSDoc for why setTimeout(0) is
  // provably late enough.
  const blurTriggerIfPointerDriven = () => {
    if (zoneRef.current?.dataset.interaction === "keyboard") return;
    setTimeout(() => {
      triggerButtonRef.current?.blur();
    }, 0);
  };

  // An open menu pins the zone revealed regardless of pointer.
  useEffect(() => {
    if (isMenuOpen) {
      setIsExpanded(true);
    }
  }, [isMenuOpen]);

  // A drag starting anywhere flips every zone's `isDisabled` — fully
  // suppress: close any open menu and hide.
  useEffect(() => {
    if (isDisabled) {
      setIsMenuOpen(false);
      setIsExpanded(false);
    }
  }, [isDisabled]);

  // Stuck-hover self-heal: browsers fire no mouseleave when an element
  // moves out from under a stationary pointer. There's no height animation
  // of this zone's own to trigger that anymore, but rows/cards elsewhere
  // can still shift when something is inserted, which is exactly the same
  // hazard — a stationary pointer can still end up over nothing. While
  // revealed and the menu is closed, watch document-level pointer movement
  // and hide immediately (no grace period; the CSS opacity fade is the only
  // softening) the moment the pointer is actually outside the zone's
  // current, live-measured bounds. Only one zone is ever revealed at a time
  // in practice, so at most one of these listeners is live; it detaches the
  // moment the zone hides, the menu opens (pinned), isDisabled flips true,
  // or the component unmounts — all via this same effect's cleanup, since
  // each of those is a dependency change.
  useEffect(() => {
    if (!isExpanded || isMenuOpen || isDisabled) return;

    const handlePointerMove = (event: PointerEvent) => {
      const rect = zoneRef.current?.getBoundingClientRect();
      if (!rect) return;
      const isInsideZone =
        event.clientX >= rect.left - STUCK_HOVER_TOLERANCE_PX &&
        event.clientX <= rect.right + STUCK_HOVER_TOLERANCE_PX &&
        event.clientY >= rect.top - STUCK_HOVER_TOLERANCE_PX &&
        event.clientY <= rect.bottom + STUCK_HOVER_TOLERANCE_PX;
      if (isInsideZone) return;
      isPointerOverRef.current = false;
      setIsExpanded(false);
    };

    document.addEventListener("pointermove", handlePointerMove);
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
    };
  }, [isExpanded, isMenuOpen, isDisabled]);

  const handleMouseEnter = () => {
    isPointerOverRef.current = true;
    if (isDisabled) return;
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    isPointerOverRef.current = false;
    if (isMenuOpen) return; // stays pinned open regardless of pointer
    setIsExpanded(false);
  };

  // DropdownMenu's own close path (Escape, outside click) — item selection
  // uses the more specific handleSelect below instead, which clears state
  // immediately rather than waiting on this callback's timing.
  const handleOpenChange = (nextIsOpen: boolean) => {
    setIsMenuOpen(nextIsOpen);
    if (!nextIsOpen) {
      if (!isPointerOverRef.current) {
        setIsExpanded(false);
      }
      blurTriggerIfPointerDriven();
    }
  };

  // Clicking the zone (but not the plus itself — that already toggles via
  // DropdownMenu's own trigger click, and handling it again here would just
  // toggle it right back) opens/closes the menu too, so the whole hit area
  // is a trigger, not just the plus.
  const handleZoneClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    if (triggerWrapRef.current?.contains(e.target as Node)) return;
    setIsMenuOpen((current) => !current);
  };

  // Wraps each menu action: insert, then clear every bit of reveal state
  // (see the linger-bug note in the JSDoc above) instead of relying on
  // DropdownMenu's own close to eventually get there.
  const handleSelect = (action: () => void) => () => {
    action();
    setIsMenuOpen(false);
    setIsExpanded(false);
    isPointerOverRef.current = false;
    blurTriggerIfPointerDriven();
  };

  const zoneClassName = [
    styles.zone,
    isExpanded ? styles.expanded : "",
    isDisabled ? styles.disabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  const iconSize: IconSize = variant === "compact" ? "sm" : "md";

  return (
    <div
      ref={zoneRef}
      className={zoneClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleZoneClick}
      onPointerDown={handleZonePointerDown}
      onKeyDown={handleZoneKeyDown}
      onFocus={handleZoneFocus}
    >
      <div className={styles.band}>
        <div ref={triggerWrapRef} className={styles.trigger}>
          <DropdownMenu
            isMenuOpen={isMenuOpen}
            onOpenChange={handleOpenChange}
            placement="below"
            alignment="center"
            button={{
              label: "Insert",
              icon: <Icon icon={Plus} color="secondary" size={iconSize} />,
              isIconOnly: true,
              variant: "ghost",
              size: "sm",
              isDisabled,
              ref: triggerButtonRef,
            }}
            items={[
              { label: "Question", onClick: handleSelect(onInsertQuestion) },
              { label: "Section", onClick: handleSelect(onInsertSection) },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default InsertZone;
